import React, { useCallback, useMemo, useRef, useState } from 'react';

import type { FrapiTeaserItem } from '../../../../types/frapi-types.js';
import type { TeaserListProps } from '../../../components/teasers/TeaserList.js';
import type { TopListView } from './types.js';

import {
  TopListHeader,
  TopListHero,
  TopListMap,
  TopListTeaserList,
} from './components/index.js';
import { useTopListMap } from './hooks/useTopListMap.js';
import { filterWithCoordinates, toMapMarkers } from './utils.js';

type TopListProps = {
  title: string;
  description: string;
  teasers: FrapiTeaserItem[];
  relatedTopListTeasers?: TeaserListProps[];
};

const TopList: React.FC<TopListProps> = ({
  title,
  description,
  teasers,
  relatedTopListTeasers,
}) => {
  const [view, setView] = useState<TopListView>('map');

  const toggleView = useCallback(
    (): void => setView((current) => (current === 'map' ? 'list' : 'map')),
    [],
  );

  const teaserRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const {
    currentArticleId,
    setCurrentArticleId,
    handleMarkerClick,
    handleTeaserView,
  } = useTopListMap(teaserRefs);

  const teasersWithCoords = useMemo(
    () => filterWithCoordinates(teasers),
    [teasers],
  );

  const mapMarkers = useMemo(
    () => toMapMarkers(teasersWithCoords),
    [teasersWithCoords],
  );

  return (
    <div className="top-list">
      <TopListHeader title={title} view={view} onViewToggle={toggleView} />
      {view === 'list' && (
        <TopListHero title={title} description={description} />
      )}
      <TopListMap
        active={view === 'map'}
        markers={mapMarkers}
        currentArticleId={currentArticleId}
        onMarkerClick={handleMarkerClick}
        initialLocation={teasersWithCoords[0]?.restaurantCoordinates}
      />
      <TopListTeaserList
        teasers={teasersWithCoords}
        relatedTopListTeasers={relatedTopListTeasers}
        view={view}
        teaserRefs={teaserRefs}
        {...(view === 'map'
          ? {
              onTeaserView: handleTeaserView,
              onRelatedTopListTeaserView: () => setCurrentArticleId(null),
            }
          : {})}
      />
    </div>
  );
};

export { TopList };
